import { ApiItemType } from '@highimpact-frontend/api-types';
import AddIcon from '@mui/icons-material/Add';
import { Box, IconButton, Tabs } from '@mui/material';
import React, { useEffect, useState } from 'react';
import PermissionDecoratorV2 from './PermissionDecoratorV2';
import { NavigationTab } from './NavigationTab';

export interface TabInfo {
  name: string;
  onClick: () => void;
  renameable?: boolean;
  onRename?: (newName: string) => void;
  deletable?: boolean;
  onDelete?: () => void;
}

export interface NavigationTabBarProps {
  tabs: TabInfo[];
  currentTabIndex: number;
  onNew?: () => void;
  projectId?: number;
  siteId?: number;
  simplifiedView?: boolean;
  // Permission keys from ApiItemType for CRUD buttons
  itemNameCreateBtn?: ApiItemType;
  itemNameDeleteBtn?: ApiItemType;
  itemNameEditEl?: ApiItemType;
}

/**
 * Renders a row of tabs with optional rename/delete actions, plus an “add new” button.
 */
export const NavigationTabBar: React.FC<NavigationTabBarProps> = ({
  tabs,
  currentTabIndex,
  onNew,
  itemNameCreateBtn,
  projectId,
  siteId,
  itemNameDeleteBtn,
  itemNameEditEl,
}) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingName, setEditingName] = useState<string>('');

  useEffect(() => {
    setEditingIndex(null);
  }, [currentTabIndex]);

  const handleStartEditing = (index: number) => {
    setEditingIndex(index);
    setEditingName(tabs[index].name);
  };

  const handleCommitEditing = (index: number) => {
    const tab = tabs[index];
    if (tab.renameable && tab.onRename) {
      tab.onRename(editingName);
    }
    setEditingIndex(null);
  };

  const handleCancelEditing = () => {
    setEditingIndex(null);
  };

  return (
    <Box display="flex" alignItems="center">
      <Tabs
        value={currentTabIndex}
        onChange={(_, newIndex) => tabs[newIndex].onClick()}
        aria-label="navigation tab bar"
        variant="scrollable"
        scrollButtons="auto"
      >
        {tabs.map((tab, idx) => (
          <NavigationTab
            key={idx}
            name={tab.name}
            selected={idx === currentTabIndex}
            renameable={tab.renameable}
            onRename={(newName) => tab.onRename?.(newName)}
            deletable={tab.deletable}
            onDelete={() => tab.onDelete?.()}
            onClick={() => tab.onClick()}
            isEditing={editingIndex === idx}
            editingName={editingName}
            onEditingNameChange={(val) => setEditingName(val)}
            onStartEditing={() => handleStartEditing(idx)}
            onCommitEditing={() => handleCommitEditing(idx)}
            onCancelEditing={handleCancelEditing}
            itemNameDeleteBtn={itemNameDeleteBtn}
            projectId={projectId}
            siteId={siteId}
            itemNameEditEl={itemNameEditEl}
          />
        ))}
      </Tabs>

      {onNew && itemNameCreateBtn ? (
        <PermissionDecoratorV2
          item={itemNameCreateBtn}
          project_id={projectId}
          site_id={siteId}
        >
          <IconButton onClick={onNew} aria-label="add new tab" size="large">
            <AddIcon />
          </IconButton>
        </PermissionDecoratorV2>
      ) : onNew ? (
        <IconButton onClick={onNew} aria-label="add new tab" size="large">
          <AddIcon />
        </IconButton>
      ) : null}
    </Box>
  );
};
